'use client';

import * as React from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { loadVmv, saveVmv } from '@/lib/vmv';
import type { VmvSet } from '@helix/lib';

type Status = 'loading' | 'ready' | 'error';

type FieldErrors = {
  vision?: string;
  mission?: string;
  values?: string;
};

type ValueRow = {
  id: number;
  text: string;
};

export function VmvEditor() {
  const searchParams = useSearchParams();

  const [status, setStatus] = React.useState<Status>('loading');
  const [vision, setVision] = React.useState('');
  const [mission, setMission] = React.useState('');
  const [values, setValues] = React.useState<ValueRow[]>([{ id: 1, text: '' }]);
  const [errors, setErrors] = React.useState<FieldErrors>({});
  const [isSaving, setIsSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = React.useState(false);

  const nextIdRef = React.useRef(2);

  const applyVmv = React.useCallback(
    (vmv: VmvSet | null) => {
      if (!vmv) {
        setVision('');
        setMission('');
        setValues([{ id: 1, text: '' }]);
        nextIdRef.current = 2;
        return;
      }

      setVision(vmv.vision.body ?? '');
      setMission(vmv.mission.body ?? '');

      if (vmv.values.length === 0) {
        setValues([{ id: 1, text: '' }]);
        nextIdRef.current = 2;
      } else {
        const mapped = vmv.values.map((value, index) => ({
          id: index + 1,
          text: value.title || value.body || '',
        }));
        setValues(mapped);
        nextIdRef.current = mapped.length + 1;
      }
    },
    [setVision, setMission, setValues],
  );

  const fetchVmv = React.useCallback(async () => {
    const simulateError = searchParams?.get('simulateError') === 'true';
    setStatus('loading');
    setSaveError(null);
    setSaveSuccess(false);

    if (simulateError) {
      setStatus('error');
      return;
    }

    try {
      const vmv = await loadVmv();
      applyVmv(vmv);
      setStatus('ready');
    } catch {
      setStatus('error');
    }
  }, [searchParams, applyVmv]);

  React.useEffect(() => {
    void fetchVmv();
  }, [fetchVmv]);

  const handleAddValue = () => {
    const id = nextIdRef.current++;
    setValues((current) => [...current, { id, text: '' }]);
  };

  const handleRemoveValue = (id: number) => {
    setValues((current) => {
      const remaining = current.filter((row) => row.id !== id);
      if (remaining.length === 0) {
        const nextId = nextIdRef.current++;
        return [{ id: nextId, text: '' }];
      }
      return remaining;
    });
  };

  const handleValueChange = (id: number, text: string) => {
    setValues((current) => current.map((row) => (row.id === id ? { ...row, text } : row)));
  };

  const validate = (): boolean => {
    const trimmedVision = vision.trim();
    const trimmedMission = mission.trim();
    const nonEmptyValues = values.map((v) => v.text.trim()).filter(Boolean);

    const fieldErrors: FieldErrors = {};
    if (!trimmedVision) {
      fieldErrors.vision = 'Vision is required.';
    }
    if (!trimmedMission) {
      fieldErrors.mission = 'Mission is required.';
    }
    if (nonEmptyValues.length === 0) {
      fieldErrors.values = 'Add at least one value.';
    }

    setErrors(fieldErrors);
    return Object.keys(fieldErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaveError(null);
    setSaveSuccess(false);

    if (!validate()) {
      return;
    }

    const payload = {
      vision: vision.trim(),
      mission: mission.trim(),
      values: values.map((v) => v.text.trim()).filter(Boolean),
    };

    setIsSaving(true);
    try {
      const updated = await saveVmv(payload);
      applyVmv(updated);
      setSaveSuccess(true);
    } catch {
      setSaveError('We could not save your VMV. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRetry = () => {
    void fetchVmv();
  };

  if (status === 'error') {
    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <h1 className="text-2xl font-semibold text-foreground">Vision, Mission, and Values</h1>
          <p className="text-sm text-muted-foreground">
            This is your company&apos;s north star. Everything else in Helix will anchor to it.
          </p>
        </div>

        <div className="flex justify-center">
          <div className="w-full max-w-xl rounded-xl border border-border bg-card text-foreground shadow-sm">
            <div className="border-b border-border px-6 py-4">
              <h2 className="text-lg font-semibold">Vision, Mission, Values</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                We couldn&apos;t load your VMV data. Make sure the API is running and the database
                is seeded, then try again.
              </p>
            </div>
            <div className="px-6 py-4 text-sm text-muted-foreground">
              <p className="text-xs">
                For local setup instructions, see <span className="font-mono">docs/phase1_VMV.md</span>.
              </p>
            </div>
            <div className="flex items-center justify-between gap-3 border-t border-border px-6 py-4">
              <button
                type="button"
                onClick={handleRetry}
                className="inline-flex items-center rounded-md bg-foreground px-4 py-2 text-sm font-medium text-background transition-colors hover:opacity-90"
              >
                Retry
              </button>
              <Link
                href="/"
                className="text-sm font-medium text-foreground underline underline-offset-4"
              >
                Back to Overview
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const isLoading = status === 'loading';

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h1 className="text-2xl font-semibold text-foreground">Vision, Mission, and Values</h1>
        <p className="text-sm text-muted-foreground">
          This is your company&apos;s north star. Everything else in Helix will anchor to it.
        </p>
      </div>

      <div className="mx-auto w-full max-w-3xl rounded-xl border border-border bg-card text-card-foreground shadow-sm">
        <form onSubmit={handleSubmit} className="space-y-6 p-6">
          <div className="space-y-1">
            <label htmlFor="vision" className="text-sm font-medium text-foreground">
              Vision
            </label>
            <p className="text-xs text-muted-foreground">
              A clear picture of the future you&apos;re building. For example, &quot;Design confident
              organizations.&quot;
            </p>
            <textarea
              id="vision"
              value={vision}
              onChange={(event) => setVision(event.target.value)}
              className="mt-2 min-h-[96px] w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground shadow-sm outline-none ring-0 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-background focus:ring-foreground disabled:opacity-50"
              disabled={isLoading || isSaving}
            />
            {errors.vision && (
              <p className="text-xs text-red-600" role="alert">
                {errors.vision}
              </p>
            )}
          </div>

          <div className="space-y-1">
            <label htmlFor="mission" className="text-sm font-medium text-foreground">
              Mission
            </label>
            <p className="text-xs text-muted-foreground">
              How you&apos;ll deliver on the vision day to day. For example, &quot;Ship tools that make change
              safe.&quot;
            </p>
            <textarea
              id="mission"
              value={mission}
              onChange={(event) => setMission(event.target.value)}
              className="mt-2 min-h-[96px] w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground shadow-sm outline-none ring-0 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-background focus:ring-foreground disabled:opacity-50"
              disabled={isLoading || isSaving}
            />
            {errors.mission && (
              <p className="text-xs text-red-600" role="alert">
                {errors.mission}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <div className="space-y-1">
              <p className="text-sm font-medium text-foreground">Values</p>
              <p className="text-xs text-muted-foreground">
                3–7 is typical. Start with at least one. Keep each value short and specific.
              </p>
            </div>

            <div className="space-y-3">
              {values.map((row, index) => (
                <div key={row.id} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={row.text}
                    onChange={(event) => handleValueChange(row.id, event.target.value)}
                    placeholder={`Value ${index + 1}`}
                    className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground shadow-sm outline-none focus:border-transparent focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-background focus:ring-foreground disabled:opacity-50"
                    disabled={isLoading || isSaving}
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveValue(row.id)}
                    className="inline-flex items-center rounded-md border border-border bg-background px-2 py-1 text-xs font-medium text-foreground transition-colors hover:bg-muted disabled:opacity-50"
                    disabled={isLoading || isSaving}
                    aria-label={`Remove value ${index + 1}`}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>

            {errors.values && (
              <p className="text-xs text-red-600" role="alert">
                {errors.values}
              </p>
            )}

            <button
              type="button"
              onClick={handleAddValue}
              className="mt-1 inline-flex items-center rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-muted disabled:opacity-50"
              disabled={isLoading || isSaving}
            >
              <span className="mr-1 text-sm" aria-hidden>
                +
              </span>
              Add value
            </button>
          </div>

          <div className="flex flex-col gap-3 border-t border-border pt-4 text-sm">
            <div className="flex items-center gap-3">
              <button
                type="submit"
                className="inline-flex items-center rounded-md bg-foreground px-4 py-2 text-sm font-medium text-background transition-colors hover:opacity-90 disabled:opacity-50"
                disabled={isSaving || isLoading}
              >
                {isSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
            {saveSuccess && (
              <p className="text-xs text-muted-foreground">
                Saved. You can refine this at any time.
              </p>
            )}
            {saveError && (
              <p className="text-xs text-red-600" role="alert">
                {saveError}
              </p>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
